import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot } from 'firebase/firestore';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions, storage } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import * as React from 'react';
import type {
  SaveVerificationDocInput,
  SetMyProfileInput,
  VerificationDocType,
} from '@cp3/shared';

// ── Profile mutate ──────────────────────────────────────────────────────

export function useSetMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetMyProfileInput) => {
      const fn = httpsCallable<SetMyProfileInput, { ok: true; changed: number }>(
        functions,
        'setMyProfile',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-profile'] }),
  });
}

// ── Verification docs ───────────────────────────────────────────────────

export interface VerificationDocRow {
  type: VerificationDocType;
  fileName: string;
  fileType: string;
  sizeBytes: number;
  storagePath: string;
  uploadedAt: Date | null;
  documentDate: Date | null;
}

/**
 * Live subscription to the user's own verification-docs subcollection.
 * Reads stay in the client (RAM only), no admin-leak — Firestore rules
 * already gate this to the owner.
 */
export function useMyVerificationDocs() {
  const { user } = useUser();
  const [items, setItems] = React.useState<VerificationDocRow[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    const col = collection(firestore, `users/${user.uid}/verificationDocs`);
    const unsub = onSnapshot(
      col,
      (snap) => {
        setItems(
          snap.docs.map((d) => {
            const data = d.data() as Record<string, unknown>;
            return {
              type: d.id as VerificationDocType,
              fileName: (data.fileName as string) ?? '',
              fileType: (data.fileType as string) ?? '',
              sizeBytes: (data.sizeBytes as number) ?? 0,
              storagePath: (data.storagePath as string) ?? '',
              uploadedAt: tsToDate(data.uploadedAt),
              documentDate: tsToDate(data.documentDate),
            };
          }),
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [user]);

  return { items, loading };
}

export function useUploadVerificationDoc() {
  const { user } = useUser();
  return useMutation({
    mutationFn: async (input: {
      type: VerificationDocType;
      file: File;
      documentDate?: Date | null;
    }) => {
      if (!user) throw new Error('Nicht angemeldet.');
      const ext = pickExtension(input.file);
      const storagePath = `users/${user.uid}/verification/${input.type}.${ext}`;
      const ref = storageRef(storage, storagePath);
      await uploadBytes(ref, input.file, { contentType: input.file.type });

      // Metadata is persisted via the server callable so any admin
      // `verifiedAt` flag stays put across re-uploads and the
      // storagePath is anchored to the caller's namespace.
      const meta: SaveVerificationDocInput = {
        type: input.type,
        storagePath,
        fileName: input.file.name,
        fileType: input.file.type,
        sizeBytes: input.file.size,
        documentDateMs:
          input.documentDate instanceof Date ? input.documentDate.getTime() : null,
      };
      const fn = httpsCallable<SaveVerificationDocInput, { ok: true }>(
        functions,
        'saveVerificationDoc',
      );
      await fn(meta);
      return { ok: true as const };
    },
  });
}

export function useDeleteVerificationDoc() {
  const { user } = useUser();
  return useMutation({
    mutationFn: async (type: VerificationDocType) => {
      if (!user) throw new Error('Nicht angemeldet.');
      const fn = httpsCallable<{ type: VerificationDocType }, { ok: true; deleted: boolean }>(
        functions,
        'deleteVerificationDoc',
      );
      await fn({ type });
      return { ok: true as const };
    },
  });
}

export function useDownloadUrl(storagePath: string | null) {
  return useQuery({
    queryKey: ['storage-url', storagePath] as const,
    enabled: !!storagePath,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!storagePath) return '';
      const url = await getDownloadURL(storageRef(storage, storagePath));
      return url;
    },
  });
}

function pickExtension(f: File): string {
  if (f.type === 'application/pdf') return 'pdf';
  if (f.type === 'image/png') return 'png';
  if (f.type === 'image/webp') return 'webp';
  if (f.type === 'image/jpeg' || f.type === 'image/jpg') return 'jpg';
  // Strip from file name as a fallback.
  const m = /\.([a-z0-9]{2,5})$/i.exec(f.name);
  return (m?.[1] ?? 'bin').toLowerCase();
}

function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  const obj = v as { toDate?: () => Date; seconds?: number };
  if (obj.toDate) return obj.toDate();
  if (typeof obj.seconds === 'number') return new Date(obj.seconds * 1000);
  if (v instanceof Date) return v;
  return null;
}
